import React, {memo, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {IWorkerState} from '../../model/workerState';
import styled from 'styled-components';
import {
   ControlButton,
   TscButtonType,
   TscThemeNames,
   TextInput,
   Spinner,
   TscThemeName,
   TscStyles
} from '@techsmith/tsc-cloud-style-guide';
import {useTscTheme} from '../../context/themeProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import cssConstants from '../../constants/cssConstants';
import {IMemberDetailsWithProfileImageModel, ITeamModel, IUserProfileWithEmailModel} from '../../model/appModel';
import config from '../../service/config';
import Constants, {GroupRoleTypes} from '../../constants/Constants';
import MemberInviteContainer from '../team/MemberInviteContainer';
import MemberAlreadyExistsError from '../../class/memberExistsError';
import {useUserQuotaStore} from '../../context/userQuotaStore';
import {EntitlementType} from '../../model/userQuotas';
import {TeamInfoMessageContainer, InfoType} from '../team/TeamInfoMessageContainer';
import teamApi from '../../service/teamApi';
import {StyledWarning, TeamsModalFormFieldsRow, TeamsModalLabel} from '../util/StyledElements';
import {CollaborationRole, ICollaborationInvitationSendQueryParams, ICollaborationResourceRoleModel} from '../../service/collaborationInvitationApi';
import {useTeamsStore} from '../../context/teamsStore';
import {useShallow} from 'zustand/react/shallow';

const ModalBody = styled.div`
   padding: 1rem;
`;

const ModalFormFieldsContainer = styled.div`
    width: 100%;
`;

const ActionsButtonContainer = styled.div`
    display:flex;
    justify-content: flex-end;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const LoadingSpinner = styled.div<{themeName: TscThemeNames}>`
   position: absolute;
   top: 0;
   left: 0;
   display: flex;
   align-items: center;
   height: 100%;
   width: 100%;
   z-index: 3;
   opacity: .9;
   border-bottom-left-radius: ${TscStyles.border.radius.xxl};
   border-bottom-right-radius: ${TscStyles.border.radius.xxl};
   background: ${props => props.themeName === TscThemeName.dawn ? TscStyles.color.ui.dawn.lightMedium : TscStyles.color.ui.dusk.dark};
`;

const SpinnerOuter = styled.div`
   width: 100%;
   margin-top: -2rem;
`;
const LoadingSpinnerStatusMessageContainer = styled.div`
   position: absolute;
   width:100%;
   margin-top: 4rem;
`;

const LoadingSpinnerStatusMessage = styled.span<{themeName: TscThemeNames}>`
   margin: 0 auto;
   color: ${props => props.themeName === TscThemeName.dawn ? TscStyles.color.text.medium : TscStyles.color.text.light};
`;

export interface ICreateTeamModalState extends IWorkerState {
   teamName: string;
   members: IMemberDetailsWithProfileImageModel[];
   isChildWorking: boolean;
   errorText: string;
   workingStatusText: string;
   teamNameErrorText: string;
   hasMaxNumberOfTeams: boolean;
   teamId: string;
}

export const CreateTeamModalBase: React.FC<ICreateTeamModalProps> = ({onClose}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [modalState, setModalState] = useState<ICreateTeamModalState>({
      isWorking: false,
      hasError: false,
      teamName: '',
      members: [],
      isChildWorking: false,
      errorText: '',
      workingStatusText: '',
      teamNameErrorText: '',
      hasMaxNumberOfTeams: false,
      teamId: ''
   });
   const quotaEntitlementType = useUserQuotaStore(s => s.QuotaEntitlementType);

   const updateState = (updatedValues: Partial<ICreateTeamModalState>): void => {
      setModalState(prevState => Object.assign({}, prevState, updatedValues));
   };

   const {createTeam, userTeamsFolderTrees} = useTeamsStore(useShallow(s => ({
      createTeam: s.createTeam,
      userTeamsFolderTrees: s.userTeamsFolderTrees
   })));

   const userIsFreeUser = quotaEntitlementType === EntitlementType.free;

   const maxNumberOfTeams = userIsFreeUser ? config.freeMaxNumberOfTeams : config.proMaxNumberOfTeams;
   const maxNumberOfTeamsPromptText = userIsFreeUser ? t('team.general.maxNumberOfTeamsFreeUser') : t('team.general.maxNumberOfTeamsProUser');

   const maxNumberOfMembers = userIsFreeUser ? config.freeTeamsMaxNumberOfMembers : config.proTeamsMaxNumberOfMembers;
   const displayMemberLimitThreshold = userIsFreeUser ? config.freeTeamsShowMemberLimitThreshold : config.proTeamsShowMemberLimitThreshold;
   const maxNumberOfMembersPromptText = userIsFreeUser ? t('team.general.maxNumberOfMembersFreeUser') : t('team.general.maxNumberOfMembersProUser');
   const quotasButtonLabel = userIsFreeUser ? t('team.buttons.upgradeToPro') : t('team.buttons.contactUs');
   const quotasLimitButtonAction = userIsFreeUser ? () => upgradeToPro() : () => contactUs();

   useEffect(() => {
      const loggedInUserAsMember: IMemberDetailsWithProfileImageModel = {
         EmailAddress: config.user.EmailAddress,
         DisplayName: config.user.DisplayName,
         profileImageUrl: config.user.AvatarImageUrl,
         Role: GroupRoleTypes.manager,
         FirstAccessDate: null,
         TechSmithId: config.user.TechSmithId,
         PendingInviteStatus: false
      };

      const initialMembers: IMemberDetailsWithProfileImageModel[] = [loggedInUserAsMember];
      updateState({members: initialMembers});
   }, []);

   useEffect(() => {
      if (userTeamsFolderTrees !== null) {
         updateState({hasMaxNumberOfTeams: userTeamsFolderTrees?.filter(userTeam => userTeam?.CreatorTechSmithId === config.user.TechSmithId).length >= maxNumberOfTeams});
      }
   }, [maxNumberOfTeams, userTeamsFolderTrees]);

   const onNameChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      const newTeamName = evt.target.value;
      const isEmpty = !newTeamName.trim();
      const isTooLong = newTeamName.length > Constants.maxTeamNameLength;
      const errorText = isEmpty ? t('team.modal.rename.errorNameEmpty') : isTooLong ? t('team.modal.rename.errorNameTooLong', {maxLength: Constants.maxCollectionNameLength}) : '';
      updateState({teamName: isTooLong ? modalState.teamName : newTeamName, hasError: isEmpty || isTooLong, teamNameErrorText: errorText});
   };

   const onCreateTeamButtonClick = async () => {
      let teamId = modalState.teamId;
      if (!teamId) {
         updateState({isWorking: true, hasError: false, workingStatusText: t('team.modal.create.statusCreatingTeam')});

         try {
            const team: ITeamModel = await teamApi.createTeam(modalState.teamName);
            updateState({teamId: team.TeamId});
            // use locally scoped variable to combat asynchronous state updates in react.
            // we need this at the end of this method and sometimes it's modalState.teamId evaluates to an empty string
            teamId = team.TeamId;

            team.Role = GroupRoleTypes.manager;
            await createTeam(team);
         } catch (exception) {
            updateState({isWorking: false, hasError: true, errorText: t('team.modal.create.errorCreatingTeam')});
            return;
         }
      }

      const hasMembersToInvite = modalState.members.some(m => m.TechSmithId !== config.user.TechSmithId);

      if (hasMembersToInvite) {
         const resourceModel: ICollaborationResourceRoleModel = {
            ResourceId: teamId,
            CollaborationRole: CollaborationRole.Editor,
            CollaborationResourceType: 'Team'
         };

         updateState({isWorking: true, hasError: false, workingStatusText: t('team.modal.create.statusInvitingMembers')});

         try {
            const invitations: ICollaborationInvitationSendQueryParams[] = modalState.members.filter(m => m.TechSmithId !== config.user.TechSmithId)
               .map(m => ({IsResend: false, RecipientEmailAddress: m.EmailAddress, CollaborationResourceRoles: [resourceModel]}));

            for (const invitation of invitations) {
               const recipientEmailAddress = invitation.RecipientEmailAddress;
               await teamApi.inviteTeamMember(teamId, recipientEmailAddress);
            }
         } catch (exception) {
            updateState({isWorking: false, hasError: true, errorText: t('team.modal.create.errorInvitingMembers')});
            return;
         }
      }

      updateState({isWorking: false, hasError: false});
      global.window.location.href = `${Constants.navigation.teamsLink}/${teamId}`;
      onClose();
   };

   const removeFromMembers = (member: IMemberDetailsWithProfileImageModel): Promise<void> => new Promise(resolve => {
      const memberIndex = modalState.members.findIndex(m => m.EmailAddress === member.EmailAddress);
      if (memberIndex > 0) {
         const membersCopy = Object.assign([], modalState.members);
         membersCopy.splice(memberIndex, 1);
         setModalState({...modalState, members: membersCopy});
         updateState({members: membersCopy});
      }
      resolve();
   });

   const addMember = (member: IUserProfileWithEmailModel): Promise<void> => new Promise((resolve, reject) => {
      const exists: boolean = modalState.members.some(m => m.EmailAddress === member.email);

      if (!exists) {
         const decoratedMember: IMemberDetailsWithProfileImageModel = {
            DisplayName: member.displayName,
            EmailAddress: member.email,
            FirstAccessDate: null,
            TechSmithId: member.techSmithId,
            profileImageUrl: member.profileImageUrl,
            Role: GroupRoleTypes.contributor,
            PendingInviteStatus: true
         };

         setModalState(prevState => ({
            ...prevState,
            members: [...prevState.members, decoratedMember]
         }));

         resolve();
      } else {
         reject(new MemberAlreadyExistsError(t('team.modal.create.memberAlreadyExists', {email: member.email})));
      }
   });

   const updateMember = (member: IUserProfileWithEmailModel) => {
      setModalState(prevState => {
         const updatedMembers = prevState.members.map(m => {
            if (m.EmailAddress === member.email) {
               const updatedMember: IMemberDetailsWithProfileImageModel = {
                  ...m,
                  EmailAddress: member.email,
                  DisplayName: member.displayName,
                  TechSmithId: member.techSmithId,
                  FirstAccessDate: m.FirstAccessDate,
                  Role: m.Role,
                  profileImageUrl: member.profileImageUrl
               };
               return updatedMember;
            }
            return m;
         });

         return {
            ...prevState,
            members: updatedMembers
         };
      });
   };

   const contactUs = () => {
      global.window.open(config.proTeamsContactUsForMoreLink, '_blank');
   };

   const upgradeToPro = () => {
      global.window.open(config.environmentData.ScreencastProPurchaseLinkUrl, '_blank');
   };

   return (
      <BasicModalPortal
         testId="create-team-modal"
         visible={true}
         title={t('team.modal.create.title')}
         width={cssConstants.manageCollectionModalWidth}
         themeName={theme}
         onClose={onClose}
      >
         <ModalBody>
            {modalState.isWorking && (
               <LoadingSpinner themeName={theme}>
                  <SpinnerOuter>
                     <Spinner themeName={theme} />
                  </SpinnerOuter>
                  <LoadingSpinnerStatusMessageContainer>
                     <LoadingSpinnerStatusMessage themeName={theme}>
                        {modalState.workingStatusText}
                     </LoadingSpinnerStatusMessage>
                  </LoadingSpinnerStatusMessageContainer>
               </LoadingSpinner>
            )}
            {!modalState.isWorking && modalState.hasMaxNumberOfTeams && (
               <TeamsModalFormFieldsRow>
                  <TeamInfoMessageContainer
                     infoType={InfoType.Warning}
                     promptText={maxNumberOfTeamsPromptText}
                     buttonLabel={quotasButtonLabel}
                     buttonAction={quotasLimitButtonAction}
                  />
               </TeamsModalFormFieldsRow>
            )}
            {modalState.members.length >= maxNumberOfMembers && (
               <TeamsModalFormFieldsRow>
                  <TeamInfoMessageContainer
                     infoType={InfoType.Warning}
                     promptText={maxNumberOfMembersPromptText}
                     buttonLabel={quotasButtonLabel}
                     buttonAction={quotasLimitButtonAction}
                  />
               </TeamsModalFormFieldsRow>
            )}
            {modalState.hasError && modalState.errorText && (
               <TeamsModalFormFieldsRow>
                  <TeamInfoMessageContainer
                     infoType={InfoType.Error}
                     promptText={modalState.errorText}
                     hideButton={true}
                     showCloseGlyph={true}
                     onCloseGlyphClick={() => updateState({hasError: false, errorText: ''})}
                  />
               </TeamsModalFormFieldsRow>
            )}
            <ModalFormFieldsContainer>
               <TeamsModalFormFieldsRow>
                  <TeamsModalLabel>
                     {t('team.modal.create.nameTextBoxLabel')}
                  </TeamsModalLabel>
                  <TextInput
                     testId="team-name-textfield"
                     disabled={modalState.hasMaxNumberOfTeams}
                     value={modalState.teamName}
                     onChange={onNameChange}
                     themeName={theme}
                     placeholder={t('team.modal.create.nameTextBoxPlaceholder')}
                     required={true}
                     tabIndex={0}
                  />
                  {modalState.teamNameErrorText && (
                     <StyledWarning id="error-message">
                        {modalState.teamNameErrorText}
                     </StyledWarning>
                  )}
               </TeamsModalFormFieldsRow>
               <MemberInviteContainer
                  labelText="Members"
                  members={modalState.members}
                  isWorking={modalState.isChildWorking}
                  disableFormFields={modalState.hasMaxNumberOfTeams}
                  memberInputSubMessage={t('team.modal.create.membersTextBoxBottomMessage')}
                  maxMemberTooltip={userIsFreeUser ? t('team.modal.create.teamLimitTooltipFree') : t('team.modal.create.teamLimitTooltip')}
                  membersCount={modalState.members.length}
                  membersLimit={maxNumberOfMembers}
                  hideMemberLimit={modalState.members.length < displayMemberLimitThreshold}
                  memberPendingInviteText={t('team.modal.create.pendingInviteStatusDisplay')}
                  removeMember={removeFromMembers}
                  addMember={addMember}
                  updateMember={updateMember}
               />
               <ActionsButtonContainer>
                  <ControlButton
                     label={t('team.modal.create.cancelButtonLabel')}
                     title={t('team.modal.create.cancelButtonLabel')}
                     buttonType={TscButtonType.secondary}
                     onClick={onClose}
                     themeName={theme}
                     width="7rem"
                  />
                  <ControlButton
                     testId="create-team-button"
                     label={t('team.modal.create.createTeamButtonLabel')}
                     title={t('team.modal.create.createTeamButtonLabel')}
                     buttonType={TscButtonType.primary}
                     onClick={onCreateTeamButtonClick}
                     themeName={theme}
                     width="7rem"
                     disabled={modalState.teamName.length === 0 || modalState.hasMaxNumberOfTeams}
                  />
               </ActionsButtonContainer>
            </ModalFormFieldsContainer>
         </ModalBody>
      </BasicModalPortal>
   );
};

export interface ICreateTeamModalProps {
   onClose(): void;
}

export default memo(CreateTeamModalBase);